import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * EntitySidebar - Generic entity overview panel
 *
 * Architecture:
 * - EntitySidebar owns the shared Drawer wrapper (Drawer, DrawerContent, DrawerHeader, DrawerBody)
 * - Entity-specific content components render inside the DrawerBody
 * - Each entity type has its own content component (ProductContent, etc.)
 *
 * Phase 1: Only 'product' entity type is fully implemented.
 *
 * TODO: Future tickets to implement:
 * - Project entity rendering (ProjectDetailContent - placeholder complete)
 * - Model entity rendering (ModelDetailContent - placeholder complete)
 * - Style entity rendering (StyleDetailContent - placeholder complete)
 * - Expression entity rendering (ExpressionDetailContent - placeholder complete)
 */
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerBody, Text } from '@nike/podium';
import { useEntityImages } from '~/hooks/useEntityImages';
import { capitalize } from '~/utils/strings';
import { getEntityBadges } from '~/utils/entityDetailUtils';
import ImageGallery from './ImageGallery';
import ProductOverviewContent from './ProductOverviewContent';
import ModelOverviewContent from './ModelOverviewContent';
import StyleOverviewContent from './StyleOverviewContent';
import ProjectOverviewContent from './ProjectOverviewContent';
import ExpressionOverviewContent from './ExpressionOverviewContent';
// ============================================================================
// Placeholder Content (For Unimplemented Entity Types)
// ============================================================================
/**
 * Placeholder content shown for entity types not yet implemented.
 */
function PlaceholderContent({ entityType }) {
    return (_jsxs(Text, { children: ["Detail view for ", entityType, " entities will be implemented in a future update."] }));
}
// ============================================================================
// Content Router
// ============================================================================
/**
 * Routes to the appropriate content component based on entity type.
 * Each entity type has its own dedicated *Content component.
 * Note: Images are rendered by parent EntitySidebar, not individual content components.
 */
function EntityContentRouter({ entity, entityType }) {
    switch (entityType) {
        case 'product':
            return _jsx(ProductOverviewContent, { product: entity });
        case 'model':
            return _jsx(ModelOverviewContent, { model: entity });
        case 'style':
            return _jsx(StyleOverviewContent, { style: entity });
        case 'project':
            return _jsx(ProjectOverviewContent, { project: entity });
        case 'expression':
            return _jsx(ExpressionOverviewContent, { expression: entity });
        default:
            return _jsx(PlaceholderContent, { entityType: entityType });
    }
}
// ============================================================================
// Main Component
// ============================================================================
/**
 * Get entity ID for image gallery tracking.
 * Each entity type uses a different ID field.
 */
function getEntityId(entity, entityType) {
    switch (entityType) {
        case 'product':
            return String(entity.productId);
        case 'model':
            return String(entity.modelId);
        case 'style':
            return entity.styleNumber;
        case 'project':
            return String(entity.id);
        case 'expression':
            return String(entity.id);
        default:
            return 'unknown';
    }
}
/**
 * Get the entity name for display in the sidebar title.
 * Falls back to undefined if no name field is available.
 */
function getEntityName(entity, entityType) {
    switch (entityType) {
        case 'product': {
            const product = entity;
            return product.styleName || undefined;
        }
        case 'model': {
            const model = entity;
            return model.modelName || undefined;
        }
        case 'style': {
            const style = entity;
            return style.styleName || undefined;
        }
        case 'project': {
            const project = entity;
            return project.projectName || undefined;
        }
        case 'expression': {
            const expression = entity;
            return expression.expressionName || undefined;
        }
        default:
            return undefined;
    }
}
/**
 * EntitySidebar - Main component that owns the shared Drawer wrapper.
 * Delegates content rendering to entity-specific components.
 * Images are rendered here for all entity types (generalized layout).
 */
function EntitySidebar({ open, onOpenChange, entity, entityType }) {
    const { images } = useEntityImages(entity, entityType);
    if (!entity)
        return null;
    const entityId = getEntityId(entity, entityType);
    const badges = getEntityBadges(entity, entityType);
    const entityName = getEntityName(entity, entityType);
    const displayTitle = entityName || `${capitalize(entityType)} Overview`;
    return (_jsx(Drawer, { open: open, onOpenChange: onOpenChange, children: _jsxs(DrawerContent, { side: "right", width: "480px", children: [_jsxs(DrawerHeader, { children: [_jsx(DrawerTitle, { className: "text-3xl font-bold", children: displayTitle }), _jsxs(DrawerDescription, { children: [capitalize(entityType), " overview and information"] })] }), _jsx(DrawerBody, { children: _jsxs("div", { className: "space-y-8", children: [_jsx(ImageGallery, { images: images, entityId: entityId, badges: badges }), _jsx(EntityContentRouter, { entity: entity, entityType: entityType })] }) })] }) }));
}
export default EntitySidebar;
